import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ModalBlank from '../components/ModalBlank';
import PlanSelection from '../account/Plans';
import { useUser } from '../userContext';
import { ArrowRightIcon, RocketLaunchIcon, CheckCircleIcon, SparklesIcon, TrophyIcon, StarIcon } from '@heroicons/react/20/solid';
import { SquareLoader } from 'react-spinners';
import { auth } from '../firebase';
import { onAuthStateChanged, sendEmailVerification } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';

const SubscriptionModal = ({
  showModal,
  setShowModal,
  handleSignOut,
  onClose,
}) => {
  const [currentStep, setCurrentStep] = useState('welcome');
  const [planRedirectLoading, setPlanRedirectLoading] = useState(false);
  const { subscriptionStatus } = useUser();
  const navigate = useNavigate();
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationTimer, setVerificationTimer] = useState(0);
  const [notice, setNotice] = useState('');
  const [autoCheckingVerification, setAutoCheckingVerification] = useState(false);

  // Load gaming font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Share+Tech+Mono:wght@400&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  // Determine modal type and initial step based on user situation
  useEffect(() => {
    if (showModal) {
      if (window.location.search.includes('session_id')) {
        if (['active', 'trialing'].includes(subscriptionStatus)) {
          setCurrentStep('subscriptionSuccess');
          setTimeout(() => {
            setCurrentStep('thankYou');
          }, 1000);
        } else {
          setCurrentStep('planSelection');
        }
      } else if (subscriptionStatus === 'expired') {
        setCurrentStep('welcomeBack');
      } else if (subscriptionStatus === 'payment_overdue') {
        setCurrentStep('paymentIssue');
      } else {
        setCurrentStep('welcome');
      }
    }
  }, [subscriptionStatus, showModal]);

  // Auto email verification checking
  useEffect(() => {
    if (showModal && auth.currentUser) {
      const isDev = process.env.REACT_APP_FIREBASE_PROJECT_ID === 'smilesflowdev-1514f';
      setIsEmailVerified(isDev ? true : auth.currentUser.emailVerified);
      
      // Set up auto verification checking
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
          if (isDev || user.emailVerified) {
            setIsEmailVerified(true);
            setAutoCheckingVerification(false);
            if (currentStep === 'emailVerification') {
              setCurrentStep('planSelection');
            }
            setNotice('');
          } else {
            setIsEmailVerified(false);
            if (currentStep === 'welcome' && !isDev) {
              setCurrentStep('emailVerification');
            }
          }
        }
      });

      // Auto-check verification every 3 seconds if on verification step
      let verificationInterval;
      if (!isEmailVerified && currentStep === 'emailVerification') {
        setAutoCheckingVerification(true);
        verificationInterval = setInterval(async () => {
          if (auth.currentUser) {
            try {
              await auth.currentUser.reload();
              if (auth.currentUser.emailVerified) {
                setIsEmailVerified(true);
                setAutoCheckingVerification(false);
                setCurrentStep('planSelection');
              }
            } catch (error) {
              // Silent error handling
            }
          }
        }, 3000);
      }

      return () => {
        unsubscribe();
        if (verificationInterval) {
          clearInterval(verificationInterval);
        }
      };
    }
  }, [showModal, currentStep, isEmailVerified]);

  const handleGoToDashboard = () => {
    const currentUrl = window.location.href;
    const newUrl = currentUrl.split('/success')[0];
    window.history.replaceState({}, '', newUrl);
    setShowModal(false);
  };

  const handleResendVerification = async () => {
    try {
      await sendEmailVerification(auth.currentUser);
      setVerificationSent(true);
      setVerificationTimer(60);
      setNotice('Verification email sent! Check your inbox.');
    } catch (error) {
      setNotice('Error sending verification email. Please try again.');
    }
  };

  useEffect(() => {
    let timer;
    if (verificationTimer > 0) {
      timer = setInterval(() => {
        setVerificationTimer(prev => prev - 1);
      }, 1000);
    } else {
      setVerificationSent(false);
    }
    return () => clearInterval(timer);
  }, [verificationTimer]);

  const features = [
    { title: "White Label Games", desc: "Fully customizable games with your branding, colors, and domain" },
    { title: "Custom CTAs", desc: "Add your own call-to-action buttons and lead capture forms" },
    { title: "Advanced Analytics", desc: "Track player behavior, conversion rates, and engagement metrics" },
    { title: "Unlimited Customization", desc: "Complete control over game design, content, and user experience" }
  ];

  const renderProgressBar = () => {
    const steps = ['welcome', 'emailVerification', 'planSelection', 'thankYou'];
    const currentIndex = steps.indexOf(currentStep);
    const progress = ((currentIndex + 1) / steps.length) * 100;

    return (
      <div className="w-full bg-gray-700 rounded-full h-2 mb-6">
        <motion.div 
          className="h-2 rounded-full"
          style={{
            background: 'linear-gradient(135deg, #F1C40F, #E67E22)'
          }}
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
    );
  };

  const renderModalContent = () => {
    // More accurate detection of subscription history
    const hasNeverSubscribed = subscriptionStatus === 'inactive' && !['expired', 'payment_overdue', 'canceled'].includes(subscriptionStatus);
    const isReturningUser = ['expired', 'payment_overdue', 'canceled'].includes(subscriptionStatus);

    if (planRedirectLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-20" style={{
          backgroundColor: '#2C3E50'
        }}>
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 border-4 rounded-full border-gray-600 border-t-yellow-400 mb-4"
          />
          <p className="text-sm text-yellow-400 font-bold" style={{
            fontFamily: '"Share Tech Mono", monospace'
          }}>PREPARING YOUR GAME STUDIO...</p>
        </div>
      );
    }

    const contentStyle = {
      backgroundColor: '#2C3E50',
      color: '#ECF0F1',
      fontFamily: '"Share Tech Mono", monospace'
    };

    switch (currentStep) {
      case 'welcome':
        return (
          <div className="relative overflow-hidden rounded-t-lg" style={contentStyle}>
            <div className="p-8 text-center">
              {!isEmailVerified && (
                <div className="mb-4">
                  {renderProgressBar()}
                </div>
              )}
              
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-6"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center border-4"
                     style={{
                       background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                       borderColor: '#F1C40F'
                     }}>
                  <RocketLaunchIcon className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold mb-3" style={{ 
                  color: '#F1C40F',
                  textShadow: '2px 2px 0px #B7950B'
                }}>
                  WELCOME TO THE GAME STUDIO
                </h2>
                <p className="text-base mb-6" style={{ color: '#ECF0F1' }}>
                  Create unlimited engaging games, track performance with advanced analytics, and grow your audience like never before.
                </p>
              </motion.div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                    className="p-3 rounded-lg border-2"
                    style={{
                      backgroundColor: '#34495E',
                      borderColor: '#2C3E50'
                    }}
                  >
                    <h4 className="text-sm font-bold mb-1" style={{ color: '#F1C40F' }}>
                      {feature.title}
                    </h4>
                    <p className="text-xs" style={{ color: '#BDC3C7' }}>
                      {feature.desc}
                    </p>
                  </motion.div>
                ))}
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full py-4 rounded-lg font-bold text-lg shadow-lg transition-all duration-200 border-2 flex items-center justify-center gap-3"
                style={{
                  background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                  borderColor: '#F1C40F',
                  color: '#FFFFFF',
                  textShadow: '1px 1px 0px #A93226'
                }}
                onClick={() => isEmailVerified ? setCurrentStep('planSelection') : setCurrentStep('emailVerification')}
              >
                                 <SparklesIcon className="w-5 h-5" />
                 {hasNeverSubscribed ? 'START 7-DAY FREE TRIAL' : isReturningUser ? 'REACTIVATE ACCOUNT' : 'GET STARTED'}
                 <ArrowRightIcon className="w-5 h-5" />
              </motion.button>

              <div className="mt-4 pt-4 border-t border-gray-600">
                <button
                  className="text-xs hover:text-yellow-300 transition-colors"
                  style={{ color: '#BDC3C7' }}
                  onClick={handleSignOut}
                >
                  Sign in with a different account
                </button>
              </div>
            </div>
          </div>
        );

      case 'welcomeBack':
        return (
          <div className="relative overflow-hidden rounded-t-lg" style={contentStyle}>
            <div className="p-8 text-center">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-6"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center border-4"
                     style={{
                       background: 'linear-gradient(135deg, #3498DB, #2980B9)',
                       borderColor: '#F1C40F'
                     }}>
                  <TrophyIcon className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold mb-3" style={{ 
                  color: '#F1C40F',
                  textShadow: '2px 2px 0px #B7950B'
                }}>
                  WELCOME BACK, CHAMPION!
                </h2>
                <p className="text-base mb-6" style={{ color: '#ECF0F1' }}>
                  Your subscription has expired, but your games are waiting! Reactivate to continue building your gaming empire.
                </p>
              </motion.div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full py-4 rounded-lg font-bold text-lg shadow-lg transition-all duration-200 border-2 flex items-center justify-center gap-3"
                style={{
                  background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                  borderColor: '#F1C40F',
                  color: '#FFFFFF',
                  textShadow: '1px 1px 0px #A93226'
                }}
                onClick={() => setCurrentStep('planSelection')}
              >
                <RocketLaunchIcon className="w-5 h-5" />
                REACTIVATE MY ACCOUNT
                <ArrowRightIcon className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        );

      case 'paymentIssue':
        return (
          <div className="relative overflow-hidden rounded-t-lg" style={contentStyle}>
            <div className="p-8 text-center">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-6"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center border-4"
                     style={{
                       background: 'linear-gradient(135deg, #F39C12, #E67E22)',
                       borderColor: '#F1C40F'
                     }}>
                  <SparklesIcon className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold mb-3" style={{ 
                  color: '#F1C40F',
                  textShadow: '2px 2px 0px #B7950B'
                }}>
                  PAYMENT UPDATE NEEDED
                </h2>
                <p className="text-base mb-6" style={{ color: '#ECF0F1' }}>
                  There's a small issue with your payment method. Update it to keep your gaming studio running smoothly!
                </p>
              </motion.div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full py-4 rounded-lg font-bold text-lg shadow-lg transition-all duration-200 border-2 flex items-center justify-center gap-3"
                style={{
                  background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                  borderColor: '#F1C40F',
                  color: '#FFFFFF',
                  textShadow: '1px 1px 0px #A93226'
                }}
                onClick={() => setCurrentStep('planSelection')}
              >
                <CheckCircleIcon className="w-5 h-5" />
                UPDATE PAYMENT METHOD
                <ArrowRightIcon className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        );

      case 'emailVerification':
        return (
          <div className="relative overflow-hidden rounded-t-lg" style={contentStyle}>
            <div className="p-8 text-center">
              <div className="mb-4">
                {renderProgressBar()}
              </div>

              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-6"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center border-4"
                     style={{
                       background: 'linear-gradient(135deg, #9B59B6, #8E44AD)',
                       borderColor: '#F1C40F'
                     }}>
                  <SparklesIcon className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-xl font-bold mb-3" style={{ 
                  color: '#F1C40F',
                  textShadow: '2px 2px 0px #B7950B'
                }}>
                  EMAIL VERIFICATION
                </h2>
                <p className="text-sm mb-4" style={{ color: '#ECF0F1' }}>
                  Check your inbox and click the verification link. We'll automatically detect when you're verified!
                </p>
              </motion.div>

              {autoCheckingVerification && (
                <motion.div
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="mb-4 p-3 rounded-lg border-2"
                  style={{
                    backgroundColor: '#34495E',
                    borderColor: '#F1C40F'
                  }}
                >
                  <p className="text-sm font-bold" style={{ color: '#F1C40F' }}>
                    🔍 AUTO-CHECKING VERIFICATION...
                  </p>
                </motion.div>
              )}

              {notice && (
                <div className="mb-4 p-3 rounded-lg" style={{
                  backgroundColor: notice.includes('Error') ? '#E74C3C' : '#27AE60',
                  color: '#FFFFFF'
                }}>
                  <p className="text-sm font-bold">{notice}</p>
                </div>
              )}

              <div className="space-y-3">
                {!verificationSent ? (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleResendVerification}
                    className="w-full py-3 rounded-lg font-bold border-2 transition-all duration-200"
                    style={{
                      backgroundColor: '#34495E',
                      borderColor: '#F1C40F',
                      color: '#F1C40F'
                    }}
                  >
                    RESEND VERIFICATION EMAIL
                  </motion.button>
                ) : (
                  <div className="p-3 rounded-lg" style={{
                    backgroundColor: '#34495E',
                    color: '#BDC3C7'
                  }}>
                    <p className="text-sm">
                      Verification email sent! Wait {verificationTimer}s to resend.
                    </p>
                  </div>
                )}

                <button
                  className="text-xs hover:text-yellow-300 transition-colors"
                  style={{ color: '#BDC3C7' }}
                  onClick={handleSignOut}
                >
                  Sign in with a different account
                </button>
              </div>
            </div>
          </div>
        );

      case 'subscriptionSuccess':
        return (
          <div className="flex flex-col items-center justify-center py-20" style={contentStyle}>
            <motion.div 
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-16 h-16 border-4 rounded-full border-gray-600 border-t-yellow-400 mb-4"
            />
            <p className="text-sm font-bold" style={{ color: '#F1C40F' }}>
              ACTIVATING YOUR GAME STUDIO...
            </p>
          </div>
        );

      case 'planSelection':
        return (
          <div style={contentStyle}>
            <div className="px-6 pt-6 pb-2">
              <div className="mb-4">
                {renderProgressBar()}
              </div>
              
              <h2 className="text-xl font-bold mb-2" style={{ 
                color: '#F1C40F',
                textShadow: '1px 1px 0px #B7950B'
              }}>
                {subscriptionStatus === 'expired' ? 'REACTIVATE YOUR STUDIO' : 'CHOOSE YOUR GAME PLAN'}
              </h2>
                              <p className="text-sm mb-0" style={{ color: '#BDC3C7' }}>
                {isReturningUser
                  ? 'Welcome back! Select a plan to continue building amazing games.'
                  : hasNeverSubscribed 
                    ? 'Start with a 7-day free trial on any plan.'
                    : 'Choose the plan that fits your gaming goals.'}
              </p>
            </div>
            <PlanSelection isModal={true} bypassOwnerCheck={true} showTrial={hasNeverSubscribed} />
          </div>
        );

      case 'thankYou':
        return (
          <div className="text-center p-8" style={contentStyle}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center border-4"
                   style={{
                     background: 'linear-gradient(135deg, #27AE60, #2ECC71)',
                     borderColor: '#F1C40F'
                   }}>
                <CheckCircleIcon className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-3" style={{ 
                color: '#F1C40F',
                textShadow: '2px 2px 0px #B7950B'
              }}>
                GAME STUDIO ACTIVATED!
              </h2>
              <p className="text-base mb-6" style={{ color: '#ECF0F1' }}>
                🎮 Welcome to the ultimate game creation experience! Your studio is ready and we're excited to see what you'll build.
              </p>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full py-4 rounded-lg font-bold text-lg shadow-lg transition-all duration-200 border-2 flex items-center justify-center gap-3"
                style={{
                  background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                  borderColor: '#F1C40F',
                  color: '#FFFFFF',
                  textShadow: '1px 1px 0px #A93226'
                }}
                onClick={handleGoToDashboard}
              >
                <RocketLaunchIcon className="w-5 h-5" />
                LAUNCH GAME STUDIO
                <ArrowRightIcon className="w-5 h-5" />
              </motion.button>
            </motion.div>
          </div>
        );

      default:
        return null;
    }
  };

  const getModalMaxWidth = () => {
    switch (currentStep) {
      case 'planSelection':
        return 'max-w-4xl';
      case 'welcome':
      case 'welcomeBack':
      case 'paymentIssue':
        return 'max-w-lg';
      default:
        return 'max-w-md';
    }
  };

  return (
    <ModalBlank
      id="subscription-issue-modal"
      modalOpen={showModal}
      setModalOpen={setShowModal}
      maxWidth={getModalMaxWidth()}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {renderModalContent()}
        </motion.div>
      </AnimatePresence>
      
      {currentStep === 'planSelection' && !planRedirectLoading && (
        <div className="px-5 py-4 border-t text-center" style={{
          backgroundColor: '#34495E',
          borderColor: '#2C3E50'
        }}>
          <button
            className="text-xs hover:text-yellow-300 transition-colors"
            style={{ 
              color: '#BDC3C7',
              fontFamily: '"Share Tech Mono", monospace'
            }}
            onClick={handleSignOut}
          >
            Sign in with a different account
          </button>
        </div>
      )}
    </ModalBlank>
  );
};

export default SubscriptionModal;
